import React from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import { Translate } from 'react-localize-redux';
import AnimatedCheck from '../../../components/general/AnimatedCheck';
import { VisitReviewProgress } from './reviewProgress';

interface ApprovalSuccessProps {
    /**
     * The same projection the rail was reading a moment ago.
     *
     * Not a handful of loose scalars. Every number on this screen is already a field of
     * `VisitReviewProgress`, computed once over one deduplicated list — and pulling them out
     * as separate props made contradictory combinations constructible: `withFeedback: 3`
     * beside `hasFeedback: false` renders "3 returned with feedback" and "No feedback was
     * sent" on the same screen. Taking the whole object means the two facts arrive from the
     * same `outcomes` array that made them agree, and the container wiring this up
     * (SKIN-262) hands the rail and this screen the identical value instead of re-deriving a
     * second count and hoping its total matches.
     */
    progress: VisitReviewProgress;
    onBackToQueue: () => void;
}

/**
 * SKIN-242 — the confirmation that replaces the review screen after approval.
 *
 * It restates what was recorded rather than only that the request succeeded. Approval is
 * irreversible and follows several minutes of work across ~20 photographs, so the fact the
 * reviewer wants confirmed is whether anything is going back to the field team — and if the
 * answer is genuinely nothing, that is said in words. An absent line would read as a screen
 * that failed to render its counts.
 *
 * **"Nothing was sent" is said only when nothing was sent.** The count and the yes/no are
 * two different fields of the projection, not one derived from the other: a photograph count
 * cannot answer the question, because feedback about a lesion's shot set has no photograph
 * to be counted against. Do not collapse them into one.
 *
 * The way out is a single explicit action back to the queue: the visit behind this screen
 * is now LOCKED, so returning to it would land the reviewer on a page where every control
 * they just used has become static text.
 */
export default function ApprovalSuccess({ progress, onBackToQueue }: ApprovalSuccessProps) {
    const { assessed, withFeedback, hasFeedback } = progress;
    return (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', maxWidth: 480, mx: 'auto', mt: 4 }} data-testid="approval-success">
            <AnimatedCheck />

            <Typography variant="h6" sx={{ mt: 1 }}>
                <Translate id="hospital.quality-check.review.approved-title" />
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} data-testid="success-reviewed">
                {assessed}
                {' '}
                <Translate id="hospital.quality-check.review.reviewed-count" />
            </Typography>

            {/*
              * Three branches, not two. The middle one is the whole point: every photograph
              * was fine and a lesion panel still went back, which is the case the rail's
              * own "Approve with comments" label is built to catch.
              */}
            {withFeedback > 0 && (
                <Typography variant="body2" color="text.secondary" data-testid="success-feedback">
                    {withFeedback}
                    {' '}
                    <Translate id="hospital.quality-check.review.feedback-count" />
                </Typography>
            )}

            {withFeedback === 0 && hasFeedback && (
                <Typography variant="body2" color="text.secondary" data-testid="success-lesion-feedback">
                    <Translate id="hospital.quality-check.review.lesion-feedback-sent" />
                </Typography>
            )}

            {!hasFeedback && (
                <Typography variant="body2" color="text.secondary" data-testid="success-no-feedback">
                    <Translate id="hospital.quality-check.review.no-feedback-sent" />
                </Typography>
            )}

            <Box sx={{ mt: 3 }}>
                <Button variant="contained" onClick={onBackToQueue} data-testid="back-to-queue">
                    <Translate id="hospital.quality-check.review.back-to-queue" />
                </Button>
            </Box>
        </Paper>
    );
}
